"use client";
import React, { useState, useMemo, useCallback } from "react";
import { OnboardingProps } from "../../types/interfaces";
import { resolveSteps } from "../../utils/utils";
import LanguageContextProvider from "../../utils/context/languageContext";
import { ConfigProvider } from "../../utils/context/configContext";
import StepsWrapper from "./StepsWrapper";
import usePhoneDetection from "../../customHooks/usePhoneDetection";

export const Onboarding: React.FC<OnboardingProps> = ({ steps, config, onComplete, onDeviceDetected }) => {
	usePhoneDetection({ onDeviceDetected });
	const visibleSteps = useMemo(() => resolveSteps(steps), [steps]);
	const [values, setValues] = useState<Record<string, any>>({});
	const [stepIndex, setStepIndex] = useState(0);
	const currentStep = visibleSteps[stepIndex];
	// Hidden steps (isVisible:false) are placed first in stepOrder so they render
	// as pre-completed dots at the start of the progress bar. Visible steps follow
	// in their resolved (sorted) order. Because currentStep is always a visible step,
	// its indexOf position is always >= hiddenSteps.length, which means the first
	// N dots are permanently in "completed" state — no juggling, no backward jumps.
	const hiddenSteps = steps.filter((s) => s.isVisible === false);
	const stepOrder = [
		...hiddenSteps.map((s) => s.type),
		...visibleSteps.map((s) => s.type),
	];

	/** 4️⃣ Handle completion for a step */
	const handleStepComplete = useCallback(
		(value: any) => {
			if (!currentStep) return;
			const updated = { ...values, [currentStep.type]: value };
			setValues(updated);
			if (stepIndex === visibleSteps.length - 1) {
				onComplete?.(updated);
			} else {
				setStepIndex((prev) => prev + 1); // 👈 FIX
			}
		},
		[currentStep, values, stepIndex, visibleSteps.length, onComplete],
	);

	if (!visibleSteps.length) return <div>No steps configured for onboarding.</div>;

	return (
		<LanguageContextProvider>
			<ConfigProvider config={config}>
				<StepsWrapper currentStep={currentStep} onStepDone={handleStepComplete} visibleSteps={visibleSteps} stepOrder={stepOrder} />
			</ConfigProvider>
		</LanguageContextProvider>
	);
};
